import {html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type {Nullable} from '@gl/lit/types/general.js';
import type {PortalRegistryItem} from './index.js';
import type CanvasWindow from './index.js';
import {toCssValue, whenComponentIsReady} from '@gl/lit/utils/dom.js';

export type Model = {
  title: string;
  open: boolean;
  activePortal: Nullable<PortalRegistryItem>;
  portalRegistry: CanvasWindow['portalRegistry'];
};

export const Msg = makeVariants({
  open: Variant<string>()('open'),
  close: Variant<void>()('close'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init
function applySizes(host: CanvasWindow) {
  if (!host.$canvas) return;

  whenComponentIsReady(host.$canvas, () => {
    const {width, height} = host.$canvas!.getBoundingClientRect();
    host.style.setProperty('height', toCssValue(height));
    host.style.setProperty('width', toCssValue(width));
  });
}

function listenWindowResize(host: CanvasWindow) {
  window.addEventListener(
    'resize',
    () => {
      applySizes(host);
    },
    {
      signal: host.disconnectSignal,
    }
  );
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  return [
    {
      title: <string>props.get('title') ?? '',
      open: <boolean>props.get('open') ?? false,
      portalRegistry:
        <CanvasWindow['portalRegistry']>props.get('portalRegistry') ??
        new Map(),
      activePortal: null,
    } as const,
    cmd(listenWindowResize),
  ];
}

// -- Update

function update(
  this: CanvasWindow,
  msg: Msg,
  model: Model,
  cmd: Cmd
): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'open': {
      const portal = this.portalRegistry.get(msg.value);

      return [
        {
          ...model,
          open: Boolean(portal && true),
          activePortal: portal ?? null,
        } as const,
        cmd(applySizes),
      ];
    }

    case 'close':
      return {...model, open: false, activePortal: null} as const;
  }
}

// -- View

function view(this: CanvasWindow, model: Model) {
  return html`
    <!-- Shim -->
    ${when(
      model.open,
      () => html`<div class="shim"></div>`,
      () => nothing
    )}

    <!-- Window -->
    <div
      class="container ${classMap({
        'container--open': model.open,
      })}"
    >
      <div
        class="canvas-window ${classMap({
          'canvas-window--open': model.open,
        })}"
      >
        ${repeat(
          model.portalRegistry.entries(),
          ([k]) => k,
          ([k, v]) => {
            return html`
              <div
                data-key=${k}
                class="portal-contents"
                style=${styleMap({
                  display: model.activePortal?.key === k ? 'block' : 'none',
                })}
              >
                ${v.template}
              </div>
            `;
          }
        )}
      </div>
    </div>
  `;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
